import React from 'react';
import { Box, Typography, IconButton, Button, Badge, alpha, useTheme, useMediaQuery } from '@mui/material';
import { Add } from '@mui/icons-material';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { todayHeaderSx, focusVisibleSx } from './stiklainisStyles';
import ReservationBlock from './ReservationBlock';
import StiklainisDayAgenda from './StiklainisDayAgenda';
import { Reservation } from './types';
import { isToday } from './slotUtils';

const DESKTOP_VISIBLE = 3;

interface StiklainisMonthViewProps {
  currentDate: Date;
  reservations: Reservation[];
  selectedRoomId: string;
  currentUserId?: string;
  dayNames: string[];
  selectedMobileDay: Date | null;
  onSelectedMobileDayChange: (day: Date) => void;
  dayAgendaDate: Date | null;
  onOpenDayAgenda: (day: Date) => void;
  onCloseDayAgenda: () => void;
  onDayCreate: (day: Date) => void;
  onReservationClick: (reservation: Reservation, isOwn: boolean) => void;
}

const StiklainisMonthView: React.FC<StiklainisMonthViewProps> = ({
  currentDate,
  reservations,
  selectedRoomId,
  currentUserId,
  dayNames,
  selectedMobileDay,
  onSelectedMobileDayChange,
  dayAgendaDate,
  onOpenDayAgenda,
  onCloseDayAgenda,
  onDayCreate,
  onReservationClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const getReservationsForDay = (day: Date) =>
    reservations
      .filter((r) => {
        const start = new Date(r.startTime);
        return (
          r.room.id === selectedRoomId &&
          start.getFullYear() === day.getFullYear() &&
          start.getMonth() === day.getMonth() &&
          start.getDate() === day.getDate()
        );
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const agendaDay = dayAgendaDate;
  const agendaReservations = agendaDay ? getReservationsForDay(agendaDay) : [];
  const mobileDay = selectedMobileDay || todayStart;
  const mobileAgendaReservations = getReservationsForDay(mobileDay);

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderTop: 1,
          borderLeft: 1,
          borderColor: 'divider',
        }}
        role="grid"
        aria-label={t('stiklainis.monthView')}
      >
        {dayNames.map((dayName) => (
          <Box
            key={dayName}
            role="columnheader"
            sx={{
              py: 1.1,
              px: 0.5,
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'divider',
              textAlign: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'primary.dark',
                fontSize: '0.8rem',
              }}
            >
              {dayName}
            </Typography>
          </Box>
        ))}

        {calendarDays.map((day) => {
          const dayReservations = getReservationsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const dayIsToday = isToday(day);
          const isPast = new Date(day.getFullYear(), day.getMonth(), day.getDate()) < todayStart;
          const isSelectedMobile = isMobile && selectedMobileDay && isSameDay(day, selectedMobileDay);
          const overflow = dayReservations.length - DESKTOP_VISIBLE;

          const handleCellActivate = () => {
            if (isMobile) {
              onSelectedMobileDayChange(day);
              return;
            }
            onDayCreate(day);
          };

          return (
            <Box
              key={day.toISOString()}
              role="gridcell"
              tabIndex={0}
              aria-label={`${day.toLocaleDateString()}, ${dayReservations.length} ${t('stiklainis.reservationsCount')}`}
              aria-selected={Boolean(isSelectedMobile)}
              onClick={handleCellActivate}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCellActivate();
                }
              }}
              sx={{
                minHeight: isMobile ? 56 : 120,
                borderRight: 1,
                borderBottom: 1,
                borderColor: 'divider',
                p: isMobile ? 0.5 : 0.85,
                bgcolor: isSelectedMobile
                  ? alpha(theme.palette.primary.main, 0.1)
                  : isPast
                    ? alpha(theme.palette.grey[600], 0.14)
                    : 'background.paper',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: dayIsToday
                  ? `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.75)}`
                  : 'none',
                ...focusVisibleSx,
                '&:hover': {
                  bgcolor: isSelectedMobile
                    ? alpha(theme.palette.primary.main, 0.14)
                    : isPast
                      ? alpha(theme.palette.grey[600], 0.2)
                      : alpha(theme.palette.action.hover, 0.5),
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: isMobile ? 0 : 0.5,
                  gap: 0.35,
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 26,
                    height: 24,
                    px: 0.65,
                    borderRadius: 1,
                    ...(dayIsToday
                      ? todayHeaderSx(theme)
                      : {
                          bgcolor: isPast
                            ? 'transparent'
                            : alpha(theme.palette.primary.main, 0.06),
                          border: '1px solid',
                          borderColor: isPast ? 'transparent' : alpha(theme.palette.primary.main, 0.2),
                        }),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      lineHeight: 1,
                      // Only past days are muted — adjacent/future months stay bookable-looking
                      color: dayIsToday
                        ? 'primary.dark'
                        : isPast
                          ? 'text.disabled'
                          : 'text.primary',
                      opacity: !isCurrentMonth && isPast ? 0.7 : 1,
                    }}
                  >
                    {day.getDate()}
                  </Typography>
                </Box>

                {!isMobile && (
                  <IconButton
                    size="small"
                    aria-label={t('stiklainis.addReservationOnDay')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDayCreate(day);
                    }}
                    sx={{
                      width: 26,
                      height: 26,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      ...focusVisibleSx,
                    }}
                  >
                    <Add sx={{ fontSize: 14 }} />
                  </IconButton>
                )}

                {isMobile && dayReservations.length > 0 && (
                  <Badge
                    badgeContent={dayReservations.length}
                    color="primary"
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        opacity: 0.35,
                        display: 'inline-block',
                      }}
                      aria-hidden
                    />
                  </Badge>
                )}
              </Box>

              {!isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                  {dayReservations.slice(0, DESKTOP_VISIBLE).map((r) => {
                    const isOwn = Boolean(currentUserId && r.user.id === currentUserId);
                    return (
                      <ReservationBlock
                        key={r.id}
                        reservation={r}
                        isOwn={isOwn}
                        top={0}
                        height={0}
                        variant="month"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReservationClick(r, isOwn);
                        }}
                      />
                    );
                  })}
                  {overflow > 0 && (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenDayAgenda(day);
                      }}
                      aria-label={t('stiklainis.moreBookings', { count: overflow })}
                      sx={{
                        justifyContent: 'flex-start',
                        minHeight: 22,
                        px: 0.5,
                        py: 0,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        color: 'text.secondary',
                        ...focusVisibleSx,
                      }}
                    >
                      +{overflow} {t('stiklainis.more')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {isMobile && (
        <StiklainisDayAgenda
          open
          inline
          day={mobileDay}
          reservations={mobileAgendaReservations}
          currentUserId={currentUserId}
          onClose={() => {}}
          onReservationClick={onReservationClick}
          onCreate={onDayCreate}
        />
      )}

      {!isMobile && (
        <StiklainisDayAgenda
          open={Boolean(dayAgendaDate)}
          day={agendaDay}
          reservations={agendaReservations}
          currentUserId={currentUserId}
          onClose={onCloseDayAgenda}
          onReservationClick={(r, isOwn) => {
            onCloseDayAgenda();
            onReservationClick(r, isOwn);
          }}
          onCreate={(d) => {
            onCloseDayAgenda();
            onDayCreate(d);
          }}
        />
      )}
    </Box>
  );
};

export default StiklainisMonthView;
